import React from 'react';
import { createRoot } from 'react-dom/client';
import 'bootstrap/dist/css/bootstrap.min.css';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import store from './redux/store';
import { Provider } from 'react-redux';
import { fetchWithcookieData, getApiBaseUrl } from './redux/WithcookieSlice';

// PWA manifest 동적 업데이트 함수
const updateManifest = (withcookieData: any) => {
  try {
    // companySkin 정보에서 이름과 로고 추출
    let appName = 'React App'; // 기본값
    let logoUrl = '/logo192.png'; // 기본값
    
    // 1. 먼저 회사명 확인
    if (withcookieData?.skin?.company?.name) {
      appName = withcookieData.skin.company.name;
    }
    
    // 2. companySettingJson에서 추가 정보 확인
    const settingJson = withcookieData?.skin?.company?.companySettingJson;
    if (settingJson) {
      try {
        const parsedSettings = typeof settingJson === 'string' ? JSON.parse(settingJson) : settingJson;
        if (parsedSettings?.site_name) {
          appName = parsedSettings.site_name;
        }
      } catch (e) {
        console.error('Failed to parse companySettingJson:', e);
      }
    }
    
    // 3. 로고 URL 확인
    if (withcookieData?.skin?.theme?.main_logo_url) {
      logoUrl = withcookieData.skin.theme.main_logo_url;
    }
    
    // favicon 업데이트
    const favicon = document.querySelector('link[rel="icon"]') as HTMLLinkElement;
    if (favicon && withcookieData?.skin?.theme?.favicon_ico_url) {
      favicon.href = withcookieData.skin.theme.favicon_ico_url;
    }
    
    // manifest 링크 요소 찾기
    const manifestLink = document.querySelector('link[rel="manifest"]');
    if (!manifestLink) return;
    
    // 새로운 manifest 객체 생성
    const manifest = {
      "short_name": appName,
      "name": appName,
      "icons": [
        {
          "src": logoUrl,
          "sizes": "192x192",
          "type": "image/png"
        },
        {
          "src": logoUrl,
          "sizes": "512x512",
          "type": "image/png"
        }
      ],
      "start_url": ".",
      "display": "standalone",
      "theme_color": "#000000",
      "background_color": "#ffffff"
    };
    
    // Blob으로 manifest 생성
    const manifestBlob = new Blob([JSON.stringify(manifest, null, 2)], { type: 'application/json' });
    const manifestURL = URL.createObjectURL(manifestBlob);
    
    // manifest 링크 업데이트
    manifestLink.setAttribute('href', manifestURL);
    
    // apple-touch-icon 업데이트
    let appleIcon = document.querySelector('link[rel="apple-touch-icon"]');
    if (!appleIcon) {
      appleIcon = document.createElement('link');
      appleIcon.setAttribute('rel', 'apple-touch-icon');
      document.head.appendChild(appleIcon);
    }
    appleIcon.setAttribute('href', logoUrl);
    
    // title 업데이트
    document.title = appName;
    
    // theme-color 업데이트 (있다면)
    if (withcookieData?.skin?.theme?.primary_color) {
      const themeColorMeta = document.querySelector('meta[name="theme-color"]');
      if (themeColorMeta) {
        themeColorMeta.setAttribute('content', withcookieData.skin.theme.primary_color);
      }
    }
    
  } catch (error) {
    console.error('Failed to update manifest:', error);
  }
};

// withcookieData 변경 감지 및 manifest 업데이트
store.subscribe(() => {
  const state = store.getState();
  const withcookieData = state.withcookie?.withcookieData;
  
  if (withcookieData && Object.keys(withcookieData).length > 0) {
    updateManifest(withcookieData);
  }
});

// 원본 fetch API 저장
const originalFetch = window.fetch;

// API 호출 우선순위 설정
const pendingRequests: Array<{request: RequestInfo | URL, init?: RequestInit, priority: number, resolve: (value: Response) => void, reject: (reason: any) => void}> = [];
let isProcessingQueue = false;

// check_current 요청 상태 관리를 위한 변수들
let checkCurrentRequested = false; // 최초 요청 여부
let checkCurrentCompleted = false; // 요청 완료 여부
let lastCheckCurrentTime = 0;
const CHECK_CURRENT_INTERVAL = 10000; // 5초에서 10초로 증가하여 요청 빈도 줄임
let checkCurrentInProgress = false; // 현재 진행 중인지 확인

// API 요청 추적을 위한 변수들
let popupRequestInProgress = false;
let lastPopupRequestTime = 0;
const POPUP_REQUEST_INTERVAL = 2000; // 5초에서 2초로 줄임

// 중복 요청 방지를 위한 Map
const activeRequests = new Map<string, Promise<Response>>();

// check_current API가 최우선적으로 실행되도록 fetch를 패치
window.fetch = function(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  const request = input.toString();
  const apiBaseUrl = getApiBaseUrl();
  
  // /finapp 경로에서 getUserById 요청에 대한 특별한 제한
  const currentPath = window.location.pathname;
  if (currentPath === '/finapp' && request.includes('/api/users/') && request.includes('?company_id=')) {
    const cacheKey = request;
    if (activeRequests.has(cacheKey)) {
      return activeRequests.get(cacheKey)!;
    }
    
    const promise = originalFetch(input, init).then(response => {
      // 5초 후에 캐시에서 제거 (더 긴 캐시 시간)
      setTimeout(() => {
        activeRequests.delete(cacheKey);
      }, 5000);
      return response;
    }).catch(error => {
      activeRequests.delete(cacheKey);
      throw error;
    });
    
    activeRequests.set(cacheKey, promise);
    return promise;
  }
  
  // check_current 요청 자체는 항상 바로 처리 (단, 중복 방지)
  if (request.includes(`${apiBaseUrl}/api/check_current`)) {
    // 이미 진행 중인 check_current 요청이 있으면 그것을 반환
    const cacheKey = 'check_current';
    if (activeRequests.has(cacheKey)) {
      return activeRequests.get(cacheKey)!;
    }
    
    // check_current 요청 완료 표시
    checkCurrentRequested = true;
    checkCurrentInProgress = true;
    
    // 진행 중인 모든 요청 취소 (whoami, posts 등)
    while (pendingRequests.length > 0) {
      const req = pendingRequests.pop();
      if (req) {
        req.reject(new Error('Canceled by higher priority request'));
      }
    }
    
    // 원본 fetch 실행하고 완료 플래그 설정
    const promise = originalFetch(input, init).then(response => {
      checkCurrentCompleted = true;
      checkCurrentInProgress = false;
      activeRequests.delete(cacheKey); // 완료 후 캐시에서 제거
      return response;
    }).catch(error => {
      checkCurrentInProgress = false;
      activeRequests.delete(cacheKey); // 오류 발생 시에도 캐시에서 제거
      throw error;
    });
    
    // 활성 요청에 추가
    activeRequests.set(cacheKey, promise);
    return promise;
  }
  
  // 팝업 API 요청 처리 - 무한 요청 방지 로직 간소화
  if (request.includes(`/api/posts?category_code=ADDITIONAL_POPUP`)) {
    // 첫 번째 요청은 항상 허용
    if (lastPopupRequestTime === 0) {
      lastPopupRequestTime = Date.now();
      return originalFetch(input, init);
    }
    
    const currentTime = Date.now();
    
    // 짧은 시간 내에 반복 요청되는 경우만 제한
    if (currentTime - lastPopupRequestTime < POPUP_REQUEST_INTERVAL) {
      return Promise.reject(new Error('팝업 API 요청 간격 제한'));
    }
    
    // 요청 시간 업데이트
    lastPopupRequestTime = currentTime;
    return originalFetch(input, init);
  }
  
  // API 호출 우선순위 확인
  let priority = 10; // 기본 우선순위
  
  if (request.includes(`/api/users/whoami`)) {
    // whoami는 두 번째 우선순위
    priority = 2;
  } else if (request.includes(`/api/posts?category_code=ADDITIONAL_POPUP`)) {
    // popup은 세 번째 우선순위
    priority = 3;
  }
  
  // check_current 요청이 아직 요청되지 않았거나 완료되지 않은 경우에만 처리
  if (!checkCurrentCompleted && 
      !isProcessingQueue && 
      !request.includes(`${apiBaseUrl}/api/check_current`)) {
    
    const currentTime = Date.now();
    
    // check_current가 아직 요청되지 않았거나 마지막 요청으로부터 일정 시간이 지난 경우에만 요청
    if (!checkCurrentRequested || 
        (!checkCurrentInProgress && currentTime - lastCheckCurrentTime > CHECK_CURRENT_INTERVAL)) {
      lastCheckCurrentTime = currentTime;
      
      // 아직 요청된 적이 없으면 직접 요청
      if (!checkCurrentRequested) {
        checkCurrentRequested = true;
        store.dispatch(fetchWithcookieData());
      }
    }
    
    // check_current 요청이 완료될 때까지 다른 요청들은 큐에 추가
    return new Promise((resolve, reject) => {
      pendingRequests.push({
        request: input,
        init,
        priority,
        resolve: resolve as (value: Response) => void,
        reject
      });
      
      // 요청 큐 처리 시작
      if (!isProcessingQueue) {
        processRequestQueue();
      }
    });
  }
  
  // check_current가 이미 완료되었거나 다른 요청이면 바로 실행
  return originalFetch(input, init);
};

// 요청 큐 처리 함수
async function processRequestQueue() {
  if (isProcessingQueue || pendingRequests.length === 0) return;
  
  isProcessingQueue = true;
  
  try {
    // check_current 데이터 로드 대기
    await waitForCheckCurrent();
    
    // 우선순위 기준으로 요청 정렬
    pendingRequests.sort((a, b) => a.priority - b.priority);
    
    // 요청 처리
    while (pendingRequests.length > 0) {
      const req = pendingRequests.shift();
      if (!req) continue;
      
      try {
        const response = await originalFetch(req.request, req.init);
        req.resolve(response);
      } catch (error) {
        req.reject(error);
      }
      
      // 다음 요청 사이에 약간의 지연 추가
      await new Promise(resolve => setTimeout(resolve, 50));
    }
  } finally {
    isProcessingQueue = false;
  }
}

// check_current 데이터 로드 완료 대기
async function waitForCheckCurrent(maxWait = 5000) {
  const startTime = Date.now();
  
  while (store.getState().withcookie.withcookieData === null) {
    if (Date.now() - startTime > maxWait) {
      console.warn('check_current 데이터 로드 타임아웃 - 요청 처리 계속');
      break;
    }
    
    // 100ms 대기
    await new Promise(resolve => setTimeout(resolve, 100));
  }
}

// 애플리케이션 초기 로드 시 check_current API를 가장 먼저 호출 (중복 방지)
if (!checkCurrentRequested) {
  store.dispatch(fetchWithcookieData());
}

const root = createRoot(document.getElementById('root')!);
root.render(
  <React.StrictMode>
    <Provider store={store}>
      <App />
    </Provider>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();

// 미디어 쿼리를 감지하여 모바일 메뉴 버튼을 관리하는 함수 추가
window.addEventListener('load', function() {
  // 모바일 환경 감지를 위한 미디어 쿼리
  const isMobile = window.matchMedia('(max-width: 991px)').matches;
  
  // 모바일 환경이면 body에 클래스 추가
  if (isMobile) {
    document.body.classList.add('mobile-view-active');
    // 모바일 환경에서 스크롤 허용
    document.body.style.overflow = 'auto';
    document.documentElement.style.overflow = 'auto';
  }
  
  // 윈도우 리사이즈 이벤트 리스너 추가
  window.addEventListener('resize', function() {
    const isMobileNow = window.matchMedia('(max-width: 991px)').matches;
    
    if (isMobileNow) {
      document.body.classList.add('mobile-view-active');
      // 모바일 환경에서 스크롤 허용
      document.body.style.overflow = 'auto';
      document.documentElement.style.overflow = 'auto';
    } else {
      document.body.classList.remove('mobile-view-active');
    }
  });
  
  // 모바일 메뉴 버튼 클릭 이벤트 처리
  document.addEventListener('click', function(e) {
    const target = e.target as Element;
    
    // 모바일 메뉴 버튼 클릭 처리
    if (target.closest('.mobile-aside-button')) {
      const mobileMenu = document.querySelector('#offcanvas-mobile-menu');
      if (mobileMenu) {
        mobileMenu.classList.add('active');
        // 메뉴가 열렸음을 표시하는 클래스 추가
        document.body.classList.add('menu-open');
        // 메뉴가 열려도 페이지 스크롤은 유지 (메뉴 내부만 스크롤 가능하게)
        document.body.style.overflow = 'auto';
      }
    }
    
    // 모바일 메뉴 닫기 버튼 클릭 처리
    if (target.closest('.offcanvas-menu-close')) {
      const mobileMenu = document.querySelector('#offcanvas-mobile-menu');
      if (mobileMenu) {
        mobileMenu.classList.remove('active');
        // 메뉴가 닫혔음을 표시하는 클래스 제거
        document.body.classList.remove('menu-open');
        // 메뉴가 닫힐 때도 페이지 스크롤 유지
        document.body.style.overflow = 'auto';
      }
    }
  });
});
